import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LiveChatLoaderProvider, useChat } from 'react-live-chat-loader';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { DRIFT_PROVIDER_KEY, WIDGET_Z_INDEX } from 'root/src/client/v2/layout/fixtures/layout.fixtures';
import { CHAT_WIDGET_BOTTOM, FLOATING_WIDGET_ICON_CLASS, FLOATING_WIDGET_RIGHT_CLASS, FLOATING_WIDGET_SIZE_CLASS, } from 'root/src/client/v2/layout/hooks/floatingWidgetStack';
import { Spinner } from 'root/src/client/v2/common/components/ui/spinner';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const getDrift = () => window.drift;
// Set on <body> while the conversation is open; app.css reads it to let Drift's own controller back
// into the corner. See the `.drift-frame-controller` rules there.
const DRIFT_CHAT_OPEN_BODY_CLASS = 'drift-chat-open';
// Redesign live-chat launcher, bottom-right corner slot. Same Drift workspace as the legacy ChatWidget,
// but the closed state is ours instead of the vendor's.
//
// `useChat()` defaults to `loadWhenIdle: false`, so nothing from Drift is fetched until the visitor
// actually clicks; the legacy widget preloads it after about a second of idle and hands the corner to
// Drift's bubble for the rest of the visit. Here the redesign button holds the corner, and Drift's
// controller is kept invisible underneath it (in CSS, on redesign routes) until the conversation opens.
//
// While the conversation IS open, Drift's controller is revealed and this button steps aside, because
// that controller is also Drift's close control: suppressing it leaves the visitor inside a chat window
// with no way out. So the swap is deliberate, and it lasts only as long as the conversation is open.
//
// Nothing here calls `drift.api.widget.hide()`. Hiding the controller through Drift's own API also
// takes it out of Drift's internal state, and then it does not come back as the close control when the
// chat opens. Hiding it in CSS instead leaves Drift behaving exactly as it normally does.
const ChatLauncher = () => {
    const [state, loadChat] = useChat();
    // SSR and first paint render nothing: `document` only exists once mounted on the client.
    const [mounted, setMounted] = useState(false);
    // Between the click and Drift being ready, so the button can show that something is happening.
    const [pending, setPending] = useState(false);
    // Driven by our own click first and only synced from Drift's events, so the handover still happens
    // if the vendor stops emitting them. Drift also opens the chat on its own (proactive messages),
    // which is what the 'chatOpen' subscription is for.
    const [chatOpen, setChatOpen] = useState(false);
    const ready = state === 'complete';
    useEffect(() => {
        setMounted(true);
    }, []);
    useEffect(() => {
        document.body.classList.toggle(DRIFT_CHAT_OPEN_BODY_CLASS, chatOpen);
        return () => document.body.classList.remove(DRIFT_CHAT_OPEN_BODY_CLASS);
    }, [chatOpen]);
    useEffect(() => {
        var _a, _b;
        if (!ready) {
            return undefined;
        }
        const drift = getDrift();
        const onChatOpen = () => setChatOpen(true);
        const onChatClose = () => setChatOpen(false);
        (_a = drift === null || drift === void 0 ? void 0 : drift.on) === null || _a === void 0 ? void 0 : _a.call(drift, 'chatOpen', onChatOpen);
        (_b = drift === null || drift === void 0 ? void 0 : drift.on) === null || _b === void 0 ? void 0 : _b.call(drift, 'chatClose', onChatClose);
        return () => {
            var _a, _b;
            (_a = drift === null || drift === void 0 ? void 0 : drift.off) === null || _a === void 0 ? void 0 : _a.call(drift, 'chatOpen', onChatOpen);
            (_b = drift === null || drift === void 0 ? void 0 : drift.off) === null || _b === void 0 ? void 0 : _b.call(drift, 'chatClose', onChatClose);
        };
    }, [ready]);
    // The click that loaded Drift also asked for the conversation, so open it as soon as it is ready.
    // `loadChat({ open: true })` is not used for that: it opens through Drift's welcome message rather
    // than the conversation itself.
    useEffect(() => {
        var _a, _b, _c;
        if (!ready || !pending) {
            return;
        }
        setPending(false);
        (_c = (_b = (_a = getDrift()) === null || _a === void 0 ? void 0 : _a.api) === null || _b === void 0 ? void 0 : _b.openChat) === null || _c === void 0 ? void 0 : _c.call(_b);
    }, [ready, pending]);
    const handleClick = useCallback(() => {
        var _a, _b, _c;
        setChatOpen(true);
        if (ready) {
            (_c = (_b = (_a = getDrift()) === null || _a === void 0 ? void 0 : _a.api) === null || _b === void 0 ? void 0 : _b.openChat) === null || _c === void 0 ? void 0 : _c.call(_b);
            return;
        }
        setPending(true);
        loadChat({ open: false });
    }, [ready, loadChat]);
    // Stays up through `pending` so the click has visible feedback while Drift downloads; from then on
    // the open conversation is Drift's, controller included.
    if (!mounted || (chatOpen && !pending)) {
        return null;
    }
    // `.redesign` on a `contents` wrapper, and the portal to <body>, for the same reasons as the Discord
    // widget: RedesignLayout is `isolate`, and the `.redesign` color rule would beat `text-*` set on the
    // same element. See RedesignDiscordWidget.
    return createPortal(<div className="redesign contents">
			<button type="button" onClick={handleClick} disabled={pending} aria-label="Chat with us" aria-busy={pending} title="Chat with us" style={{ zIndex: WIDGET_Z_INDEX, bottom: CHAT_WIDGET_BOTTOM }} className={cn('fixed flex items-center justify-center rounded-full border-none bg-primary p-0 text-primary-foreground', 'shadow-2xl shadow-black/50 transition-transform hover:scale-105 disabled:cursor-wait disabled:hover:scale-100', FLOATING_WIDGET_SIZE_CLASS, FLOATING_WIDGET_RIGHT_CLASS, !pending && 'cursor-pointer')}>
				{pending ? <Spinner size={20}/> : <ChatBubbleIcon className={FLOATING_WIDGET_ICON_CLASS}/>}
			</button>
		</div>, document.body);
};
// Rendered by RedesignLayout on the redesign pages whose route offers live chat (see
// `isRedesignChatRoute`). The provider carries no `idlePeriod`, so even a stray `loadWhenIdle` consumer
// could not preload Drift behind the launcher.
export const RedesignChatWidget = () => (<LiveChatLoaderProvider provider="drift" providerKey={DRIFT_PROVIDER_KEY} idlePeriod={0}>
		<ChatLauncher />
	</LiveChatLoaderProvider>);
